import { gql, useMutation } from "@apollo/client";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import type { CategorySortUpdate, ServiceSortUpdate } from "../types";

// GraphQL Mutations
const CREATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation CreateServiceCategory($salonId: ID!, $input: CreateServiceCategoryInput!) {
    createServiceCategory(salonId: $salonId, input: $input) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation UpdateServiceCategory($salonId: ID!, $categoryId: ID!, $input: UpdateServiceCategoryInput!) {
    updateServiceCategory(salonId: $salonId, categoryId: $categoryId, input: $input) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
  }
`;

const ARCHIVE_SERVICE_CATEGORY_MUTATION = gql`
  mutation ArchiveServiceCategory($salonId: ID!, $categoryId: ID!) {
    archiveServiceCategory(salonId: $salonId, categoryId: $categoryId)
  }
`;

const RESTORE_SERVICE_CATEGORY_MUTATION = gql`
  mutation RestoreServiceCategory($salonId: ID!, $categoryId: ID!) {
    restoreServiceCategory(salonId: $salonId, categoryId: $categoryId)
  }
`;

const UPDATE_CATEGORY_SORT_ORDER_MUTATION = gql`
  mutation UpdateCategorySortOrder(
    $salonId: ID!
    $updates: [CategorySortUpdate!]!
  ) {
    updateCategorySortOrder(salonId: $salonId, updates: $updates)
  }
`;

const UPDATE_SERVICE_SORT_ORDER_MUTATION = gql`
  mutation UpdateServiceSortOrder(
    $salonId: ID!
    $updates: [ServiceSortUpdate!]!
  ) {
    updateServiceSortOrder(salonId: $salonId, updates: $updates)
  }
`;

const CREATE_SERVICE_MUTATION = gql`
  mutation CreateServiceFromMutation($salonId: ID!, $input: CreateServiceInput!) {
    createService(salonId: $salonId, input: $input) {
      id
      name
      description
      serviceCode
      duration
      pricingMode
      basePrice
      isActive
      allowOnlineBooking
      createdAt
      updatedAt
      category {
        id
        name
        color
      }
    }
  }
`;

const UPDATE_SERVICE_MUTATION = gql`
  mutation UpdateServiceFromMutation($salonId: ID!, $serviceId: ID!, $input: UpdateServiceInput!) {
    updateService(salonId: $salonId, serviceId: $serviceId, input: $input) {
      id
      name
      description
      serviceCode
      duration
      pricingMode
      basePrice
      isActive
      allowOnlineBooking
      createdAt
      updatedAt
      category {
        id
        name
        color
      }
    }
  }
`;

const ARCHIVE_SERVICE_MUTATION = gql`
  mutation ArchiveService($salonId: ID!, $serviceId: ID!) {
    archiveService(salonId: $salonId, serviceId: $serviceId)
  }
`;

const RESTORE_SERVICE_MUTATION = gql`
  mutation RestoreService($salonId: ID!, $serviceId: ID!) {
    restoreService(salonId: $salonId, serviceId: $serviceId)
  }
`;

// Category Mutations
export const useCreateServiceCategory = () => {
  const [createCategoryMutation] = useMutation(CREATE_SERVICE_CATEGORY_MUTATION);

  return useAsyncAction(
    async (salonId: string, input: any) => {
      const result = await createCategoryMutation({
        variables: { salonId, input },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.createServiceCategory;
    },
    {
      loadingMessage: "Creating category...",
      successMessage: "Category created successfully!",
      errorMessage: "Failed to create category",
    }
  );
};

export const useUpdateServiceCategory = () => {
  const [updateCategoryMutation] = useMutation(UPDATE_SERVICE_CATEGORY_MUTATION);

  return useAsyncAction(
    async (salonId: string, categoryId: string, input: any) => {
      const result = await updateCategoryMutation({
        variables: { salonId, categoryId, input },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.updateServiceCategory;
    },
    {
      loadingMessage: "Updating category...",
      successMessage: "Category updated successfully!",
      errorMessage: "Failed to update category",
    }
  );
};

export const useArchiveServiceCategory = () => {
  const [archiveCategoryMutation] = useMutation(ARCHIVE_SERVICE_CATEGORY_MUTATION);

  return useAsyncAction(
    async (salonId: string, categoryId: string) => {
      const result = await archiveCategoryMutation({
        variables: { salonId, categoryId },
        refetchQueries: ["ServicesWithCategories", "ArchivedServiceCategories", "ArchivedServices"],
      });
      return result.data?.archiveServiceCategory;
    },
    {
      loadingMessage: "Archiving category...",
      successMessage: "Category and all services archived successfully!",
      errorMessage: "Failed to archive category",
    }
  );
};

export const useRestoreServiceCategory = () => {
  const [restoreCategoryMutation] = useMutation(RESTORE_SERVICE_CATEGORY_MUTATION);

  return useAsyncAction(
    async (salonId: string, categoryId: string) => {
      const result = await restoreCategoryMutation({
        variables: { salonId, categoryId },
        refetchQueries: ["ServicesWithCategories", "ArchivedServiceCategories", "ArchivedServices"],
      });
      return result.data?.restoreServiceCategory;
    },
    {
      loadingMessage: "Restoring category...",
      successMessage:
        "Category and services restored successfully! Moved to bottom of active categories.",
      errorMessage: "Failed to restore category",
    }
  );
};

export const useUpdateCategorySortOrder = () => {
  const [updateSortOrderMutation] = useMutation(UPDATE_CATEGORY_SORT_ORDER_MUTATION);

  return useAsyncAction(
    async (salonId: string, updates: CategorySortUpdate[]) => {
      const result = await updateSortOrderMutation({
        variables: { salonId, updates },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.updateCategorySortOrder;
    },
    {
      loadingMessage: "Updating category order...",
      successMessage: "Category order updated successfully!",
      errorMessage: "Failed to update category order",
      showLoadingToast: false, // Don't show loading toast for drag operations
    }
  );
};

export const useUpdateServiceSortOrder = () => {
  const [updateSortOrderMutation] = useMutation(UPDATE_SERVICE_SORT_ORDER_MUTATION);

  return useAsyncAction(
    async (salonId: string, updates: ServiceSortUpdate[]) => {
      const result = await updateSortOrderMutation({
        variables: { salonId, updates },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.updateServiceSortOrder;
    },
    {
      loadingMessage: "Updating service order...",
      successMessage: "Service order updated successfully!",
      errorMessage: "Failed to update service order",
      showLoadingToast: false, // Don't show loading toast for drag operations
    }
  );
};

// Service Mutations
export const useCreateService = () => {
  const [createServiceMutation] = useMutation(CREATE_SERVICE_MUTATION);

  return useAsyncAction(
    async (salonId: string, input: any) => {
      const result = await createServiceMutation({
        variables: { salonId, input },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.createService;
    },
    {
      loadingMessage: "Creating service...",
      successMessage: "Service created successfully!",
      errorMessage: "Failed to create service",
    }
  );
};

export const useUpdateService = () => {
  const [updateServiceMutation] = useMutation(UPDATE_SERVICE_MUTATION);

  return useAsyncAction(
    async (salonId: string, serviceId: string, input: any) => {
      const result = await updateServiceMutation({
        variables: { salonId, serviceId, input },
        refetchQueries: ["ServicesWithCategories"],
      });
      return result.data?.updateService;
    },
    {
      loadingMessage: "Updating service...",
      successMessage: "Service updated successfully!",
      errorMessage: "Failed to update service",
    }
  );
};

export const useArchiveService = () => {
  const [archiveServiceMutation] = useMutation(ARCHIVE_SERVICE_MUTATION);

  return useAsyncAction(
    async (salonId: string, serviceId: string) => {
      const result = await archiveServiceMutation({
        variables: { salonId, serviceId },
        refetchQueries: ["ServicesWithCategories", "ArchivedServiceCategories", "ArchivedServices"],
      });
      return result.data?.archiveService;
    },
    {
      loadingMessage: "Archiving service...",
      successMessage: "Service archived successfully!",
      errorMessage: "Failed to archive service",
    }
  );
};

export const useRestoreService = () => {
  const [restoreServiceMutation] = useMutation(RESTORE_SERVICE_MUTATION);

  return useAsyncAction(
    async (salonId: string, serviceId: string) => {
      const result = await restoreServiceMutation({
        variables: { salonId, serviceId },
        refetchQueries: ["ServicesWithCategories", "ArchivedServiceCategories", "ArchivedServices"],
      });
      return result.data?.restoreService;
    },
    {
      loadingMessage: "Restoring service...",
      successMessage: "Service restored successfully! Moved to bottom of active services.",
      errorMessage: "Failed to restore service",
    }
  );
};
